import type { NamedFieldProps } from "shared/react";
import type { UnitType } from "shared/schema";
import { mapArray } from "shelving";
import { useStore } from "shelving/react";
import { Field, useField, useFormContext } from "shared/react";
import { UNIT_TYPES, getMainUnit, isUnitType } from "shared/schema";

/** Show a unit select field for a fixed type. */
export function UnitField({ type, ...props }: { type: UnitType } & NamedFieldProps): React.ReactElement | null {
	const field = useField<string | null>(props);
	const { name, form, value: formValue = null } = field;
	const unit = getMainUnit(type, formValue);
	useStore(form);
	const allow = UNIT_TYPES[type].allow;
	return (
		<Field {...field}>
			{allow.size > 1 ? (
				<select
					key={type} // Fully refresh if `type` changes.
					className="input space-none"
					value={unit.key} // Controlled field.
					required
					onChange={({ currentTarget }) => form.publish(name, currentTarget.value)}
				>
					<option
						value={unit.key}
						hidden // Hidden option to make sure select shows `ms` only, rather than `ms (milliseconds)` which looks weird.
					>
						{unit.abbr}
					</option>
					{mapArray(allow, ([k, t]) => (
						<option key={k} value={k}>
							{t.title}
						</option>
					))}
				</select>
			) : (
				<output
					key={type} // Fully refresh if `type` changes.
					className="input space-none"
				>
					{unit.abbr}
				</output>
			)}
		</Field>
	);
}

/**
 * Watch the `type` field in a form and show a dropdown listing corresponding units for that type.
 * - e.g. `distance` shows dropdown with `kilometer`, `meter` etc.
 */
export function TypeUnitField(props: NamedFieldProps): React.ReactElement | null {
	const form = useFormContext();
	useStore(form); // Refresh whenever the form changes because we might need to hide or show a different set of units in the select input.
	const { type } = form.value;
	return isUnitType(type) ? <UnitField type={type} {...props} /> : null;
}
